export enum MessagingStage {
  StartNewMeasurement = 'startNewMeasurement',
  RecordingStarted = 'recordingStarted',
  RecordingStopped = 'recordingStopped',
  FaceScanSuccessful = 'faceScanSuccessful',
  UserFinished = 'userFinished',
  Dismiss = 'dismiss',

  Result = 'results',
  Buckets = 'buckets',
  BucketSummaries = 'bucketSummaries',
}

interface iMessage<T> {
  stage: MessagingStage;
  uuid?: string;
  patient?: string | null;
  performer?: string | null;
  data?: T;
}

interface iMessageMetaData {
  uuid: string;
  patient: string | null;
  performer: string | null;
}

class MessagingService {
  private __targetOrigin: string;
  private metadata: iMessageMetaData;
  private v1ResultsObject: Object | null = null;
  private v1BucketsObject: Object | null = null;
  private v1BucketSummariesObject: Object | null = null;

  constructor(targetOrigin?: string) {
    this.__targetOrigin = targetOrigin ?? '*';
    this.metadata = {
      uuid: '',
      patient: null,
      performer: null,
    };
  }

  setMetadata(uuid: string, patient: string | null, performer: string | null) {
    this.metadata = {
      uuid: uuid,
      patient: patient,
      performer: performer,
    };
  }

  setV1Results(model: Object) {
    this.v1ResultsObject = model;
  }

  setV1Buckets(model: Object) {
    this.v1BucketsObject = model;
  }

  setV1BucketSummaries(model: Object) {
    this.v1BucketSummariesObject = model;
  }

  send(message: iMessage<any>) {
    if (window.parent) {
      console.info(`Post messages: Sending "${message.stage}"`);
      window.parent.postMessage(message, this.__targetOrigin);
    } else {
      console.warn(`Post messages: Parent is not detected, script was executed outside of iframe or standalone?`);
    }
  }

  startNewMeasurement() {
    this.send({
      stage: MessagingStage.StartNewMeasurement,
      data: {},
    });
  }

  recordingStarted() {
    this.send({
      stage: MessagingStage.RecordingStarted,
      uuid: this.metadata.uuid,
    });
  }

  recordingStopped() {
    this.send({
      stage: MessagingStage.RecordingStopped,
      uuid: this.metadata.uuid,
    });
  }

  faceScanSuccessful() {
    this.send({
      stage: MessagingStage.FaceScanSuccessful,
      uuid: this.metadata.uuid,
      data: {},
    });
  }

  userFinished() {
    this.send({
      stage: MessagingStage.UserFinished,
      data: {},
    });
  }

  dismiss() {
    this.send({
      stage: MessagingStage.Dismiss,
      data: {},
    });
  }

  userFinishedAndDismiss() {
    this.userFinished();
    this.dismiss();
  }

  v1Results() {
    if (this.v1ResultsObject === null) return;

    this.send({
      stage: MessagingStage.Result,
      ...this.metadata,
      data: this.v1ResultsObject,
    });
  }

  v1Buckets() {
    if (this.v1BucketsObject === null) return;

    this.send({
      stage: MessagingStage.Buckets,
      ...this.metadata,
      data: this.v1BucketsObject,
    });
  }

  v1BucketSummaries() {
    if (this.v1BucketSummariesObject === null) return;

    this.send({
      stage: MessagingStage.BucketSummaries,
      ...this.metadata,
      data: this.v1BucketSummariesObject,
    });
  }

  currentUuid(): string {
    return this.metadata.uuid;
  }
}

export const messageService = new MessagingService();
